import type { TileStyle, TileType } from '@casa/model';

// The tile types the editor offers (requirement L-13 in docs/specs/dashboard-editor.md): what each
// is for, the size a new one gets, and the form it starts in. A new tile has no data yet (its data
// is chosen in its configuration, Phase 3 E6), so it shows no data; options without a default get
// a placeholder that validates.

export type CatalogType = TileType | 'group';

export interface CatalogEntry {
    readonly type: CatalogType;
    readonly name: string;
    readonly description: string;
    /** An icon for the list, by Font Awesome name. */
    readonly icon: string;
    /** Width and height in cells on the legacy 48 × 48 grid. */
    readonly fixedSpan: readonly [number, number];
    /** Width and height in cells on a scrolling grid of 4 columns. */
    readonly scrollingSpan: readonly [number, number];
    /** Options for a new tile, where the type has options without defaults. */
    readonly options?: Readonly<Record<string, unknown>>;
    /** The style a new tile of this type starts with (a group's frame, dashboard-editor spec §5.9 QT-2). */
    readonly style?: TileStyle;
}

export const TILE_CATALOG: readonly CatalogEntry[] = [
    { type: 'value', name: 'Value', description: 'A number with its unit, optionally on a gauge or with a graph.', icon: 'gauge', fixedSpan: [7, 8], scrollingSpan: [2, 2] },
    { type: 'light', name: 'Light', description: 'A light that a tap switches on or off.', icon: 'lightbulb', fixedSpan: [4, 5], scrollingSpan: [1, 1] },
    { type: 'switch', name: 'Switch', description: 'A device that a tap switches on or off, such as a socket.', icon: 'toggle-large-on', fixedSpan: [4, 5], scrollingSpan: [1, 1] },
    {
        type: 'status',
        name: 'Status or mode',
        description: 'A state as a text or an icon; with a command, a tap changes it, such as a thermostat’s or a charger’s mode.',
        icon: 'circle-check',
        fixedSpan: [6, 6],
        scrollingSpan: [1, 1],
    },
    {
        type: 'text',
        name: 'Text',
        description: 'A text of its own, or a source’s value in words, such as a note or a device’s state.',
        icon: 'font',
        fixedSpan: [8, 4],
        scrollingSpan: [2, 1],
        // Something to see in a new tile (text-tile.md TX-6).
        options: { text: 'Text' },
    },
    {
        type: 'action',
        name: 'Action',
        description: 'A button that sends one command, such as a scene; it shows no state.',
        icon: 'hand-pointer',
        fixedSpan: [4, 5],
        scrollingSpan: [1, 1],
        options: { command: 'on' },
    },
    {
        type: 'forecast',
        name: 'Weather forecast',
        description: 'The next hours, the next days and nights, or the week, with warnings, from a weather connection.',
        icon: 'cloud-sun-rain',
        // A row of readable slots (weather.md WT-7).
        fixedSpan: [30, 16],
        scrollingSpan: [4, 2],
    },
    {
        type: 'graph',
        name: 'Graph',
        description: 'Readings over time, as lines or bars.',
        icon: 'chart-line',
        fixedSpan: [12, 10],
        scrollingSpan: [4, 2],
        options: { graph: { series: [{ source: 'primary' }] } },
    },
    {
        type: 'table',
        name: 'Table',
        description: 'Columns and rows of values and texts.',
        icon: 'table',
        fixedSpan: [12, 10],
        scrollingSpan: [4, 2],
        options: { columns: [{ label: '' }], rows: [] },
    },
    {
        type: 'thermostat',
        name: 'Thermostat',
        description: 'The temperature and the set point, which a tap changes; a thermostat\'s mode and state too.',
        icon: 'temperature-half',
        fixedSpan: [9, 15],
        scrollingSpan: [2, 2],
    },
    { type: 'airquality', name: 'Air quality', description: 'CO₂, fine dust and VOC, coloured by how good they are.', icon: 'wind', fixedSpan: [9, 8], scrollingSpan: [4, 1] },
    { type: 'solar', name: 'Solar', description: 'The flow of power between solar panels, the grid, a battery and the house.', icon: 'solar-panel', fixedSpan: [12, 16], scrollingSpan: [4, 3] },
    {
        type: 'video',
        name: 'Camera',
        description: 'A camera stream.',
        icon: 'video',
        fixedSpan: [24, 16],
        scrollingSpan: [4, 3],
        options: { url: 'rtsp://camera.invalid/' },
    },
    // A still picture from a source (image-tile.md): a camera's snapshot, the demo's garden camera.
    { type: 'image', name: 'Image', description: 'A still picture, such as a camera’s snapshot.', icon: 'image', fixedSpan: [24, 16], scrollingSpan: [4, 3] },
    {
        type: 'group',
        name: 'Group',
        description: 'A box with a title, always behind the other tiles: lay it under tiles that belong together.',
        icon: 'object-group',
        fixedSpan: [12, 10],
        scrollingSpan: [4, 1],
        // A frame, not a filled tile (the owner, QT-2): the legacy group's look, which the user may clear.
        style: { background: 'none', border: '#3a3a3a', borderWidth: 1 },
    },
];

export function catalogEntry(type: CatalogType) : CatalogEntry {
    const entry = TILE_CATALOG.find((candidate) => candidate.type === type);
    if (!entry) {
        throw new Error(`No catalog entry for tile type ${type}`);
    }

    return entry;
}

/**
 * The size of a new tile on a grid: the legacy one scaled to the grid's columns and rows on a
 * fixed grid, the 4-column one scaled to the columns on a scrolling grid (its cells are square).
 * Never more than the grid, never less than one cell.
 */
export function defaultSpan(entry: CatalogEntry, grid: { columns: number; rows?: number | undefined }) : { width: number; height: number } {
    const scaled = (cells: number, ratio: number, limit: number) : number => Math.min(limit, Math.max(1, Math.round(cells * ratio)));
    if (grid.rows === undefined) {
        const ratio = grid.columns / 4;

        return { width: scaled(entry.scrollingSpan[0], ratio, grid.columns), height: scaled(entry.scrollingSpan[1], ratio, Number.POSITIVE_INFINITY) };
    }

    return { width: scaled(entry.fixedSpan[0], grid.columns / 48, grid.columns), height: scaled(entry.fixedSpan[1], grid.rows / 48, grid.rows) };
}

/**
 * The size of a new tile for a style made for a shape (tile-styles.md TY-10): the whole cells,
 * within the grid, whose shape on the screen is nearest to `ratio` (width over height), at about
 * the area of `span`, the type's usual size. `measure` gives a span's size on the screen. Of two
 * as near, the larger.
 */
export function spanForShape(
    span: { width: number; height: number },
    ratio: number,
    grid: { columns: number; rows?: number | undefined },
    measure: (span: { width: number; height: number }) => { width: number; height: number },
) : { width: number; height: number } {
    const area = span.width * span.height;
    // A scrolling grid has no bottom: up to twice as many rows as it has columns.
    const rows = grid.rows ?? 2 * Math.max(grid.columns, span.height);
    let best = span;
    let bestScore = Number.POSITIVE_INFINITY;
    for (let width = 1; width <= grid.columns; width++) {
        for (let height = 1; height <= rows; height++) {
            const size = measure({ width, height });
            // The shape counts most; the area half as much.
            const score = Math.abs(Math.log(size.width / size.height / ratio)) + 0.5 * Math.abs(Math.log((width * height) / area));
            const isNearer = score < bestScore - 1e-9;
            const isAsNearAndLarger = Math.abs(score - bestScore) <= 1e-9 && width * height > best.width * best.height;
            if (isNearer || isAsNearAndLarger) {
                best = { width, height };
                bestScore = score;
            }
        }
    }

    return best;
}
